"use client";

import { useEffect, useId, useMemo, useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import type { CategoryFilterNavRow } from "@/lib/utils/public-category-listing-payload";
import {
	buildPublicCategoryListingPath,
	subcategoryAppliesToMain,
} from "@/lib/public-category-routes";
import {
	filterSheetGlassCheckboxClassName,
	filterSheetHeadingClassName,
	filterSheetRowLabelClassName,
} from "@/components/sheets/FilterSheet/filter-sheet-glass";

type Props = {
	mainCategories: CategoryFilterNavRow[];
	subcategories: CategoryFilterNavRow[];
	filterMainCategory: CategoryFilterNavRow;
	/** All selected main category ids (path + `categories` query) for this listing. */
	filterSelectedMainIds: string[];
	/** Selected sector (subcategory) ids; empty = all sectors. */
	filterSelectedSubcategoryIds: string[];
	/** Main category id → sub ids with ≥1 published listing in that main. Omitted or empty = no sector rows (only “All”). */
	sectorIdsByMainId?: Record<string, string[]>;
	/** When true, category/sector selections stay on the current listing URL (search results). */
	filterStayOnCurrentPath?: boolean;
};

function sortByOrder(a: CategoryFilterNavRow, b: CategoryFilterNavRow): number {
	return (a.order ?? 0) - (b.order ?? 0);
}

/** Sectors A–Z (case-insensitive), for the filter sheet list only. */
function sortSectorsByTitle(a: CategoryFilterNavRow, b: CategoryFilterNavRow): number {
	return (a.title ?? "").trim().localeCompare((b.title ?? "").trim(), undefined, { sensitivity: "base" });
}

function categoryKey(c: Pick<CategoryFilterNavRow, "_id" | "id">): string {
	return String(c._id ?? c.id ?? "");
}

function sectorAllowListForMain(
	map: Record<string, string[]> | undefined,
	mainId: string,
): string[] {
	if (!map) return [];
	const id = String(mainId);
	if (Array.isArray(map[id])) return map[id];
	const hit = Object.keys(map).find((k) => String(k) === id);
	return hit != null ? map[hit]! : [];
}

function sortMainsByNavOrder(ids: Iterable<string>, mains: CategoryFilterNavRow[]): CategoryFilterNavRow[] {
	const map = new Map(mains.map((m) => [categoryKey(m), m]));
	const list: CategoryFilterNavRow[] = [];
	for (const id of ids) {
		const m = map.get(String(id));
		if (m) list.push(m);
	}
	list.sort(sortByOrder);
	return list;
}

export default function FilterCategoryIndustryNav({
	mainCategories,
	subcategories,
	filterMainCategory,
	filterSelectedMainIds,
	filterSelectedSubcategoryIds,
	sectorIdsByMainId,
	filterStayOnCurrentPath = false,
}: Props) {
	const baseId = useId();
	const initialMainAll = filterSelectedMainIds.length === 0;

	const [mainAll, setMainAll] = useState(initialMainAll);
	const [selectedMainIds, setSelectedMainIds] = useState<Set<string>>(() =>
		new Set(filterSelectedMainIds.map(String)),
	);
	const [selectedSectorIds, setSelectedSectorIds] = useState<Set<string>>(() =>
		new Set(filterSelectedSubcategoryIds.map(String)),
	);

	const sectorSelectionKey = [...selectedSectorIds].sort().join(",");

	useEffect(() => {
		const mAll = filterSelectedMainIds.length === 0;
		setMainAll(mAll);
		setSelectedMainIds(new Set(filterSelectedMainIds.map(String)));
		setSelectedSectorIds(new Set(filterSelectedSubcategoryIds.map(String)));
	}, [
		filterSelectedMainIds,
		filterSelectedSubcategoryIds,
	]);

	const applySectorPresenceFilter =
		sectorIdsByMainId != null && Object.keys(sectorIdsByMainId).length > 0;

	const selectedMainsOrdered = useMemo(
		() => sortMainsByNavOrder(selectedMainIds, mainCategories),
		[mainCategories, selectedMainIds],
	);

	const applicableSubs = useMemo(() => {
		const sortedMainList = mainCategories.slice().sort(sortByOrder);

		if (mainAll) {
			const allowed = new Set<string>();
			if (applySectorPresenceFilter) {
				for (const arr of Object.values(sectorIdsByMainId!)) {
					for (const id of arr) allowed.add(String(id));
				}
			}
			const candidates = subcategories.filter((s) =>
				sortedMainList.some((m) => subcategoryAppliesToMain(s, m)),
			);
			return candidates
				.filter(
					(s) =>
						applySectorPresenceFilter &&
						(allowed.has(String(s._id)) ||
							(s.id != null && allowed.has(String(s.id)))),
				)
				.slice()
				.sort(sortSectorsByTitle);
		}

		if (selectedMainsOrdered.length === 0) return [];

		const allowed = new Set<string>();
		if (applySectorPresenceFilter) {
			for (const m of selectedMainsOrdered) {
				for (const id of sectorAllowListForMain(sectorIdsByMainId, categoryKey(m))) {
					allowed.add(String(id));
				}
			}
		}
		const candidates = subcategories.filter((s) =>
			selectedMainsOrdered.some((m) => subcategoryAppliesToMain(s, m)),
		);
		return candidates
			.filter(
				(s) =>
					applySectorPresenceFilter &&
					(allowed.has(String(s._id)) ||
						(s.id != null && allowed.has(String(s.id)))),
			)
			.slice()
			.sort(sortSectorsByTitle);
	}, [
		applySectorPresenceFilter,
		mainAll,
		mainCategories,
		sectorIdsByMainId,
		selectedMainsOrdered,
		subcategories,
	]);

	useEffect(() => {
		if (selectedSectorIds.size === 0) return;
		setSelectedSectorIds((prev) => {
			const next = new Set<string>();
			for (const id of prev) {
				const sid = String(id);
				const sub = subcategories.find((s) => categoryKey(s) === sid);
				if (sub && applicableSubs.some((a) => categoryKey(a) === categoryKey(sub))) {
					next.add(sid);
				}
			}
			if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
			return next;
		});
	}, [applicableSubs, sectorSelectionKey, subcategories]);

	const sortedMains = useMemo(() => mainCategories.slice().sort(sortByOrder), [mainCategories]);

	const sectorsShowAll = selectedSectorIds.size === 0;

	const navBundle = useMemo(() => {
		if (mainAll) {
			const ids = [...selectedSectorIds].sort();
			return {
				navigatePath: filterStayOnCurrentPath ? "" : "/categories/browse",
				categoriesQuery: "",
				subcategoryIdsQuery: ids.length > 0 ? ids.join(",") : "",
			};
		}
		const mainsOrdered =
			selectedMainsOrdered.length > 0
				? selectedMainsOrdered
				: [filterMainCategory];
		const anchor = mainsOrdered[0]!;
		const categoriesQuery = filterStayOnCurrentPath
			? mainsOrdered.map((c) => String(c.slug || c._id).trim()).join(",")
			: mainsOrdered.length > 1
				? mainsOrdered
						.slice(1)
						.map((c) => String(c.slug || c._id).trim())
						.join(",")
				: "";

		let navigatePath = buildPublicCategoryListingPath(anchor);
		let subcategoryIdsQuery = "";

		if (selectedSectorIds.size > 0) {
			const ids = [...selectedSectorIds];
			if (
				!filterStayOnCurrentPath &&
				mainsOrdered.length === 1 &&
				ids.length === 1
			) {
				const sid = ids[0]!;
				const sub = subcategories.find((s) => categoryKey(s) === sid);
				if (sub && subcategoryAppliesToMain(sub, anchor)) {
					navigatePath = buildPublicCategoryListingPath(anchor, sub);
				} else {
					subcategoryIdsQuery = ids.join(",");
				}
			} else {
				subcategoryIdsQuery = ids.join(",");
			}
		}

		return {
			navigatePath: filterStayOnCurrentPath ? "" : navigatePath,
			categoriesQuery,
			subcategoryIdsQuery,
		};
	}, [
		filterMainCategory,
		filterStayOnCurrentPath,
		mainAll,
		selectedMainsOrdered,
		sectorSelectionKey,
		subcategories,
		selectedSectorIds,
	]);

	const { navigatePath, categoriesQuery, subcategoryIdsQuery } = navBundle;

	const toggleMain = (id: string, checked: boolean) => {
		const k = String(id);
		setMainAll(false);
		setSelectedMainIds((prev) => {
			const next = new Set(prev);
			if (checked) next.add(k);
			else next.delete(k);
			if (next.size === 0) {
				next.add(categoryKey(filterMainCategory));
			}
			return next;
		});
	};

	const toggleSector = (id: string, checked: boolean) => {
		const k = String(id);
		setSelectedSectorIds((prev) => {
			const next = new Set(prev);
			if (checked) next.add(k);
			else next.delete(k);
			return next;
		});
	};

	return (
		<>
			<input type="hidden" name="filterNavigate" value={navigatePath} readOnly />
			<input type="hidden" name="categories" value={categoriesQuery} readOnly />
			<input type="hidden" name="subcategoryIds" value={subcategoryIdsQuery} readOnly />

			<div className="flex flex-col gap-2">
				<h3 className={filterSheetHeadingClassName}>Categories</h3>
				<div className="flex flex-col gap-2">
					<div className="grid h-6 grid-cols-[2rem_1fr] items-center">
						<Checkbox
							id={`${baseId}-main-all`}
							className={filterSheetGlassCheckboxClassName}
							checked={mainAll}
							onCheckedChange={(v) => {
								const on = v === true;
								if (on) {
									setMainAll(true);
									setSelectedMainIds(new Set());
									setSelectedSectorIds(new Set());
								} else {
									setMainAll(false);
									setSelectedMainIds(new Set([categoryKey(filterMainCategory)]));
									setSelectedSectorIds(new Set());
								}
							}}
						/>
						<label htmlFor={`${baseId}-main-all`} className={filterSheetRowLabelClassName}>
							All
						</label>
					</div>
					{sortedMains.map((cat) => {
						const k = categoryKey(cat);
						const checked = !mainAll && selectedMainIds.has(k);
						return (
							<div key={k} className="grid h-6 grid-cols-[2rem_1fr] items-center">
								<Checkbox
									id={`${baseId}-main-${k}`}
									className={filterSheetGlassCheckboxClassName}
									checked={checked}
									onCheckedChange={(v) => toggleMain(k, v === true)}
								/>
								<label htmlFor={`${baseId}-main-${k}`} className={filterSheetRowLabelClassName}>
									{cat.title}
								</label>
							</div>
						);
					})}
				</div>
			</div>

			<hr className="my-6 border-0 border-t-[0.5px] border-solid border-t-[color:var(--color-brand-primary)]" />

			<div className="flex flex-col gap-2">
				<h3 className={filterSheetHeadingClassName}>Sectors</h3>
				<div className="flex flex-col gap-2">
					<div className="grid h-6 grid-cols-[2rem_1fr] items-center">
						<Checkbox
							id={`${baseId}-sub-all`}
							className={filterSheetGlassCheckboxClassName}
							checked={sectorsShowAll}
							onCheckedChange={(v) => {
								if (v === true) {
									setSelectedSectorIds(new Set());
								} else if (applicableSubs[0]) {
									setSelectedSectorIds(new Set([categoryKey(applicableSubs[0]!)]));
								}
							}}
						/>
						<label htmlFor={`${baseId}-sub-all`} className={filterSheetRowLabelClassName}>
							All
						</label>
					</div>
					{applicableSubs.map((cat) => {
						const k = categoryKey(cat);
						const checked = !sectorsShowAll && selectedSectorIds.has(k);
						return (
							<div key={k} className="grid h-6 grid-cols-[2rem_1fr] items-center">
								<Checkbox
									id={`${baseId}-sub-${k}`}
									className={filterSheetGlassCheckboxClassName}
									checked={checked}
									onCheckedChange={(v) => toggleSector(k, v === true)}
								/>
								<label htmlFor={`${baseId}-sub-${k}`} className={filterSheetRowLabelClassName}>
									{cat.title}
								</label>
							</div>
						);
					})}
				</div>
			</div>
		</>
	);
}
